import { Component, OnInit, LOCALE_ID } from '@angular/core';
import { CommonModule, registerLocaleData } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { PracticeScheduleService } from '../../../../services/practice-schedule.service';
import { GenericCrudService } from '../../../../services/generic-crud.service';
import { AuthStateService } from '../../../../core/states/auth-state.service';
import { CustomDropdownComponent } from '../../../../shared/components/form-controls/custom-dropdown/custom-dropdown.component';
import { forkJoin } from 'rxjs';
import localeEs from '@angular/common/locales/es';

registerLocaleData(localeEs);

interface CalendarDay {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  summary: any | null;
}

interface CalendarSummary {
  total_classes: number;
  by_type: { [key: string]: number };
  by_status: { [key: string]: number };
}

@Component({
  selector: 'app-calendario',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, CustomDropdownComponent],
  templateUrl: './calendario.component.html',
  styleUrls: ['./calendario.component.css'],
  providers: [{ provide: LOCALE_ID, useValue: 'es' }]
})
export class CalendarioComponent implements OnInit {

  currentYear: number = 0;
  currentMonth: number = 0;

  calendarDays: CalendarDay[] = [];
  selectedDay: CalendarDay | null = null;
  dayDetailSchedules: any[] = [];

  monthLabel: string = '';

  filters: { office_id: number | null; teacher_id: number | null; license_id: number | null } = {
    office_id: null,
    teacher_id: null,
    license_id: null
  };

  offices: any[] = [];
  teachers: any[] = [];
  licenses: any[] = [];

  loading: boolean = false;
  loadingDetail: boolean = false;

  summaryMap: { [dateStr: string]: CalendarSummary } = {};

  /** Collapse state for license groups (expanded by default) */
  collapsedLicenses: Set<string> = new Set();

  /** Collapse state for teacher sub-groups (collapsed by default) */
  collapsedTeachers: Set<string> = new Set();
  /** Track which teachers the user explicitly expanded */
  expandedTeachers: Set<string> = new Set();
  allTeachersCollapsed: boolean = true;

  private readonly EXPAND_KEY = 'calendario_expandedTeachers';

  // --- License collapse ---

  toggleLicense(licenseKey: string): void {
    if (this.collapsedLicenses.has(licenseKey)) {
      this.collapsedLicenses.delete(licenseKey);
    } else {
      this.collapsedLicenses.add(licenseKey);
    }
  }

  isLicenseCollapsed(licenseKey: string): boolean {
    return this.collapsedLicenses.has(licenseKey);
  }

  // --- Teacher collapse ---

  toggleTeacher(teacherKey: string): void {
    if (this.collapsedTeachers.has(teacherKey)) {
      this.collapsedTeachers.delete(teacherKey);
      this.expandedTeachers.add(teacherKey);
    } else {
      this.collapsedTeachers.add(teacherKey);
      this.expandedTeachers.delete(teacherKey);
    }
    this.syncAllTeachersCollapsed();
    this.saveExpandedTeachers();
  }

  isTeacherCollapsed(teacherKey: string): boolean {
    return this.collapsedTeachers.has(teacherKey);
  }

  toggleAllTeachers(): void {
    const allTeacherKeys = this.getAllTeacherKeys();
    if (this.allTeachersCollapsed) {
      this.collapsedTeachers.clear();
      this.expandedTeachers = new Set(allTeacherKeys);
      this.allTeachersCollapsed = false;
    } else {
      allTeacherKeys.forEach(k => this.collapsedTeachers.add(k));
      this.expandedTeachers.clear();
      this.allTeachersCollapsed = true;
    }
    this.saveExpandedTeachers();
  }

  private getAllTeacherKeys(): string[] {
    const keys: string[] = [];
    this.groupedSchedules.forEach(lg => {
      lg.teacherGroups.forEach(tg => keys.push(tg.teacherKey));
    });
    return keys;
  }

  /** After loading schedules, collapse all teachers except those the user expanded */
  private applyCollapsePreference(): void {
    this.collapsedTeachers.clear();
    this.collapsedLicenses.clear();
    const allKeys = this.getAllTeacherKeys();
    allKeys.forEach(key => {
      if (!this.expandedTeachers.has(key)) {
        this.collapsedTeachers.add(key);
      }
    });
    this.syncAllTeachersCollapsed();
  }

  private syncAllTeachersCollapsed(): void {
    const allKeys = this.getAllTeacherKeys();
    this.allTeachersCollapsed = allKeys.length > 0 && allKeys.every(k => this.collapsedTeachers.has(k));
  }

  private saveExpandedTeachers(): void {
    localStorage.setItem(this.EXPAND_KEY, JSON.stringify(Array.from(this.expandedTeachers)));
  }

  private loadExpandedTeachers(): void {
    const saved = localStorage.getItem(this.EXPAND_KEY);
    if (saved) {
      const arr: string[] = JSON.parse(saved);
      this.expandedTeachers = new Set(arr);
    }
  }

  /**
   * Group the day detail schedules by license, then by teacher
   */
  get groupedSchedules(): {
    licenseName: string;
    totalCount: number;
    teacherGroups: { teacherKey: string; teacherName: string; schedules: any[] }[];
  }[] {
    if (!this.dayDetailSchedules || this.dayDetailSchedules.length === 0) return [];

    // First level: license
    const licenseMap: { [key: string]: any[] } = {};
    this.dayDetailSchedules.forEach((schedule: any) => {
      const licenseName = schedule.license?.name || 'Sin carnet';
      if (!licenseMap[licenseName]) licenseMap[licenseName] = [];
      licenseMap[licenseName].push(schedule);
    });

    return Object.keys(licenseMap).sort().map(licenseName => {
      const schedules = licenseMap[licenseName];

      // Second level: teacher within this license
      const teacherMap: { [key: string]: any[] } = {};
      schedules.forEach((s: any) => {
        const teacherName = (s.teacher || s.user)?.name || 'Sin profesor';
        if (!teacherMap[teacherName]) teacherMap[teacherName] = [];
        teacherMap[teacherName].push(s);
      });

      const teacherGroups = Object.keys(teacherMap).sort().map(teacherName => ({
        teacherKey: `${licenseName}__${teacherName}`,
        teacherName,
        schedules: teacherMap[teacherName].sort((a: any, b: any) =>
          (a.start_time || '').localeCompare(b.start_time || '')
        )
      }));

      return {
        licenseName,
        totalCount: schedules.length,
        teacherGroups
      };
    });
  }

  /**
   * Get summary counts by type for the selected day
   */
  get daySummaryByType(): { type: string; count: number; color: string }[] {
    if (!this.dayDetailSchedules || this.dayDetailSchedules.length === 0) return [];
    const counts: { [key: string]: number } = {};
    this.dayDetailSchedules.forEach((s: any) => {
      const type = s.is_exam ? 'Examen' : (s.practice_type || 'Otro');
      counts[type] = (counts[type] || 0) + 1;
    });
    return Object.keys(counts).map(type => ({
      type,
      count: counts[type],
      color: this.getTypeColor(type)
    }));
  }

  /**
   * Get the full formatted date string for the selected day
   */
  getSelectedDayLabel(): string {
    if (!this.selectedDay) return '';
    const dayNames = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
    const d = this.selectedDay.date;
    const dayName = dayNames[d.getDay()];
    const day = d.getDate();
    const month = this.monthNames[d.getMonth()].toLowerCase();
    const year = d.getFullYear();
    return `${dayName.charAt(0).toUpperCase() + dayName.slice(1)}, ${day} de ${month} ${year}`;
  }

  private readonly monthNames: string[] = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  constructor(
    private practiceScheduleService: PracticeScheduleService,
    private genericCrudService: GenericCrudService,
    private authStateService: AuthStateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const today = new Date();
    this.currentYear = today.getFullYear();
    this.currentMonth = today.getMonth();

    // Operadora: fijar su sucursal activa.
    const user = this.authStateService.currentUserValue;
    if (user?.roles?.includes('operator') && user?.active_office?.id) {
      this.filters.office_id = user.active_office.id;
    }

    this.loadExpandedTeachers();
    this.loadFilterOptions();
    this.loadMonthData();
  }

  /**
   * Load filter dropdown options from the API
   */
  loadFilterOptions(): void {
    forkJoin({
      offices: this.genericCrudService.list('/config/office/listAll'),
      teachers: this.genericCrudService.list('/user/teachers'),
      licenses: this.genericCrudService.list('/config/license/listAll')
    }).subscribe({
      next: (res) => {
        this.offices = res.offices.data || [];
        this.teachers = res.teachers.data || [];
        this.licenses = res.licenses.data || [];
      },
      error: (err) => {
        console.error('Error loading filter options:', err);
      }
    });
  }

  /**
   * Load the calendar summary data for the current month
   */
  loadMonthData(): void {
    this.loading = true;
    this.selectedDay = null;
    this.dayDetailSchedules = [];
    this.updateMonthLabel();

    const monthStr = `${this.currentYear}-${String(this.currentMonth + 1).padStart(2, '0')}`;

    const activeFilters: any = {};
    if (this.filters.office_id) activeFilters.office_id = this.filters.office_id;
    if (this.filters.teacher_id) activeFilters.teacher_id = this.filters.teacher_id;
    if (this.filters.license_id) activeFilters.license_id = this.filters.license_id;

    this.practiceScheduleService.getCalendarSummary(monthStr, activeFilters).subscribe({
      next: (res) => {
        this.summaryMap = {};
        if (res.data && Array.isArray(res.data)) {
          res.data.forEach((item: any) => {
            this.summaryMap[item.date] = {
              total_classes: item.total_classes || 0,
              by_type: item.by_type || {},
              by_status: item.by_status || {}
            };
          });
        }
        this.generateCalendarGrid();
        this.loading = false;
        this.autoSelectDay();
      },
      error: (err) => {
        console.error('Error loading calendar summary:', err);
        this.generateCalendarGrid();
        this.loading = false;
        this.autoSelectDay();
      }
    });
  }

  /**
   * Auto-select a day after loading month data:
   * - Current month: select today
   * - Other months: select the 1st
   */
  private autoSelectDay(): void {
    const todayCell = this.calendarDays.find(c => c.isToday && c.isCurrentMonth);
    if (todayCell) {
      this.selectDay(todayCell);
    } else {
      const firstDay = this.calendarDays.find(c => c.isCurrentMonth && c.dayNumber === 1);
      if (firstDay) {
        this.selectDay(firstDay);
      }
    }
  }

  /**
   * Generate the 42-cell calendar grid (6 weeks x 7 days), starting from Monday
   */
  generateCalendarGrid(): void {
    this.calendarDays = [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // First day of the current month
    const firstDayOfMonth = new Date(this.currentYear, this.currentMonth, 1);

    // Get the day of the week for the first day (0=Sunday, 1=Monday, ..., 6=Saturday)
    // Convert to Monday-based: Monday=0, Tuesday=1, ..., Sunday=6
    let dayOfWeek = firstDayOfMonth.getDay();
    const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    // Start date: the Monday of the week containing the first day of the month
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - mondayOffset);

    // Generate 42 cells
    for (let i = 0; i < 42; i++) {
      const cellDate = new Date(startDate);
      cellDate.setDate(startDate.getDate() + i);
      cellDate.setHours(0, 0, 0, 0);

      const dateStr = this.formatDateToYMD(cellDate);
      const isCurrentMonth = cellDate.getMonth() === this.currentMonth && cellDate.getFullYear() === this.currentYear;
      const isToday = cellDate.getTime() === today.getTime();

      this.calendarDays.push({
        date: cellDate,
        dayNumber: cellDate.getDate(),
        isCurrentMonth,
        isToday,
        summary: this.summaryMap[dateStr] || null
      });
    }
  }

  /**
   * Select a day and load its detail schedules
   */
  selectDay(day: CalendarDay): void {
    if (!day.isCurrentMonth) return;

    this.selectedDay = day;
    this.loadingDetail = true;

    const dateStr = this.formatDateToYMD(day.date);

    this.practiceScheduleService.getSchedules({
      start_date: dateStr,
      end_date: dateStr,
      office_id: this.filters.office_id || undefined,
      license_id: this.filters.license_id || undefined,
      teacher_ids: this.filters.teacher_id ? [this.filters.teacher_id] : undefined
    }).subscribe({
      next: (res) => {
        this.dayDetailSchedules = res.data || [];
        this.loadingDetail = false;
        this.applyCollapsePreference();
      },
      error: (err) => {
        console.error('Error loading day detail:', err);
        this.dayDetailSchedules = [];
        this.loadingDetail = false;
        this.applyCollapsePreference();
      }
    });
  }

  /**
   * Navigate to the previous month
   */
  prevMonth(): void {
    this.currentMonth--;
    if (this.currentMonth < 0) {
      this.currentMonth = 11;
      this.currentYear--;
    }
    this.loadMonthData();
  }

  /**
   * Navigate to the next month
   */
  nextMonth(): void {
    this.currentMonth++;
    if (this.currentMonth > 11) {
      this.currentMonth = 0;
      this.currentYear++;
    }
    this.loadMonthData();
  }

  /**
   * Navigate to the current month (today)
   */
  goToToday(): void {
    const today = new Date();
    this.currentYear = today.getFullYear();
    this.currentMonth = today.getMonth();
    this.loadMonthData();
  }

  /**
   * Navigate to the Cuadrante view for the week containing the given date
   */
  navigateToCuadrante(date: Date): void {
    const dateStr = this.formatDateToYMD(date);
    const queryParams: any = { date: dateStr };

    // Pass teacher filter: either the selected filter or all teachers visible in the day detail
    if (this.filters.teacher_id) {
      queryParams.teacher_ids = String(this.filters.teacher_id);
    } else if (this.groupedSchedules.length > 0) {
      // Collect unique teacher IDs from the day's schedules
      const teacherIds = new Set<number>();
      this.dayDetailSchedules.forEach((s: any) => {
        const id = s.teacher_id || (s.teacher || s.user)?.id;
        if (id) teacherIds.add(id);
      });
      if (teacherIds.size > 0) {
        queryParams.teacher_ids = Array.from(teacherIds).join(',');
      }
    }

    this.router.navigate(['/dashboard/clasesPracticas/cuadrante'], { queryParams });
  }

  /**
   * Get a Tailwind background color class for a practice type
   */
  getTypeColor(type: string): string {
    switch (type) {
      case 'Abierto':
        return '#4CAF50';
      case 'Maniobras':
        return '#FF9800';
      case 'Examen':
        return '#F44336';
      default:
        return '#9E9E9E';
    }
  }

  /**
   * Get status CSS classes
   */
  getStatusClasses(status: string): string {
    switch (status) {
      case 'Disponible': return 'bg-green-100 text-green-700';
      case 'Confirmada': return 'bg-blue-100 text-blue-700';
      case 'Reservado': return 'bg-yellow-100 text-yellow-700';
      case 'Completo': return 'bg-purple-100 text-purple-700';
      case 'Completado': return 'bg-gray-100 text-gray-600';
      case 'Cancelado': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  }

  /**
   * Update the month label displayed in the header
   */
  private updateMonthLabel(): void {
    this.monthLabel = `${this.monthNames[this.currentMonth]} ${this.currentYear}`;
  }

  /**
   * Format a Date object to YYYY-MM-DD string
   */
  private formatDateToYMD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
